import { BaseService } from "../BaseService";
import { AuthService } from "../auth/auth.service";
import { environment } from '../../../../src/environments/environment';
import { HttpClient } from "@angular/common/http";
import { Router } from '@angular/router';
import { Observable, of, throwError } from "rxjs";
import { catchError, concatMap, delay, map, retryWhen, take } from 'rxjs/operators';
import { Injectable } from "@angular/core";
import { TherapistDashboardModel } from "../../models/api/TherapistDashboardModel";
import { AdminClockedInTherapistsModel } from "../../models/api/AdminClockedInTherapistsModel";
import { BookingRequest } from "../../models/api/BookingRequest";
import { BookingResponse } from "../../models/api/BookingResponse";

@Injectable({
  providedIn: 'root'
})

export class TherapistDashboardService extends BaseService {
    private urlPart: string = 'TherapistDashboard';
    constructor(private h: HttpClient, private r: Router, private a: AuthService) {
        super(h, r, a);
       this.apiSegmentUrl = this.urlPart;
    };

  

  /// <summary>
  /// PUT: Clocks a therapist in
  /// /TherapistDashboard/clockin
  /// </summary>
  /// <returns></returns>
  public clockIn(): Observable<boolean> {

    const url = `${super.getBaseUrl() + '/clockin'}`;

    if (!environment.production)
      console.log("Therapist Dashboard:Calling " + url);

    return this.h.put<boolean>(url, null)
      .pipe(
        map(next => {
          return next;
        }),
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, count) => {
              if (count > this.RETRY_CEILING) return throwError(error);
              return this.tokenCheckAndRefresh(error);
            }),
            delay(1000),
          ),
        ),
        catchError(this.errorHandler)
      );
  }

  /// <summary>
  /// PUT: Clocks out a therapist
  /// /TherapistDashboard/clockout
  /// </summary>
  /// <returns></returns>
  public clockOut(): Observable<boolean> {

    const url = `${super.getBaseUrl() + '/clockout'}`;

    if (!environment.production)
      console.log("Therapist Dashboard:Calling " + url);

    return this.h.put<boolean>(url, null)
      .pipe(
        map(next => {
          return next;
        }),
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, count) => {
              if (count > this.RETRY_CEILING) return throwError(error);
              return this.tokenCheckAndRefresh(error);
            }),
            delay(1000),
          ),
        ),
        catchError(this.errorHandler)
      );
  }

  /// <summary>
  /// PUT: Change a therapists status to On Break
  /// /TherapistDashboard/startBreak
  /// </summary>
  /// <returns></returns>
  public startBreak(): Observable<boolean> {

    const url = `${super.getBaseUrl() + '/startbreak'}`;

    if (!environment.production)
      console.log("Therapist Dashboard:Calling " + url);

    return this.h.put<boolean>(url, null)
      .pipe(
        map(next => {
          return next;
        }),
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, count) => {
              if (count > this.RETRY_CEILING) return throwError(error);
              return this.tokenCheckAndRefresh(error);
            }),
            delay(1000),
          ),
        ),
        catchError(this.errorHandler)
      );
  }

  /// <summary>
  /// PUT: Change a therapists status to Available
  /// /TherapistDashboard/endBreak
  /// </summary>
  /// <returns></returns>
  public endBreak(): Observable<boolean> {

    const url = `${super.getBaseUrl() + '/endbreak'}`;

    if (!environment.production)
      console.log("Therapist Dashboard:Calling " + url);

    return this.h.put<boolean>(url, null)
      .pipe(
        map(next => {
          return next;
        }),
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, count) => {
              if (count > this.RETRY_CEILING) return throwError(error);
              return this.tokenCheckAndRefresh(error);
            }),
            delay(1000),
          ),
        ),
        catchError(this.errorHandler)
      );
  }

  /// <summary>
  /// GET: Gets the Admin Dashboard info
  /// /Dashboard/Admin/{lastXHours}
  /// </summary>
  /// <returns></returns>
  public getTherapistDashboard(): Observable<TherapistDashboardModel> {

    let url = `${super.getBaseUrl() + '/dashboard'}`;

    // Check if we're from the app, if so, check for a device guid
    const ua = navigator.userAgent;

    if (ua.includes('WHAppAndroid') )
    {
        // Yes, its an app and its android, so check for device string
        const match = ua.match(/deviceGuid=([a-z0-9\-]+)/i);
        const deviceGuid = match ? match[1] : null;

        if (deviceGuid)
        {
            url += `?deviceguid=${deviceGuid}&mobileos=2`;
        }
    }
    else if (ua.includes('WHAppIOS'))
    {
        // Yes, its an app, and it's ios, so check for device string
        const match = ua.match(/deviceGuid=([a-z0-9\-]+)/i);
        const deviceGuid = match ? match[1] : null;

        if (deviceGuid)
        {
          url += `?deviceguid=${deviceGuid}&mobileos=1`;
        }
    }

    if (!environment.production)
      console.log("TherapistDashboard:Calling " + url);

    return this.h.get<TherapistDashboardModel>(url)
      .pipe(
        map(next => {
          let o: TherapistDashboardModel = Object.assign(new TherapistDashboardModel(), next);
          return o;
        }),
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, count) => {
              if (count > this.RETRY_CEILING) return throwError(error);
              return this.tokenCheckAndRefresh(error);
            }),
            delay(1000),
          ),
        ),
        catchError(this.errorHandler)
      );
  }

  /// <summary>
  /// PUT: Accept a booking
  /// /TherapistDashboard/acceptbooking/{bookingid}
  /// </summary>
  /// <returns></returns>
  public acceptBooking(bookingid: number): Observable<boolean> {

    const url = `${super.getBaseUrl() + '/acceptbooking/' + bookingid}`;

    if (!environment.production)
      console.log("Therapist Dashboard:Calling " + url);

    return this.h.put<boolean>(url, null)
      .pipe(
        map(next => {
          return next;
        }),
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, count) => {
              if (count > this.RETRY_CEILING) return throwError(error);
              return this.tokenCheckAndRefresh(error);
            }),
            delay(1000),
          ),
        ),
        catchError(this.errorHandler)
      );
  }

  /// <summary>
  /// PUT: Decline a booking
  /// /TherapistDashboard/declinebooking/{bookingid}
  /// </summary>
  /// <returns></returns>
  public declineBooking(bookingid: number): Observable<boolean> {

    const url = `${super.getBaseUrl() + '/declinebooking/' + bookingid}`;

    if (!environment.production)
      console.log("Therapist Dashboard:Calling " + url);

    return this.h.put<boolean>(url, null)
      .pipe(
        map(next => {
          return next;
        }),
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, count) => {
              if (count > this.RETRY_CEILING) return throwError(error);
              return this.tokenCheckAndRefresh(error);
            }),
            delay(1000),
          ),
        ),
        catchError(this.errorHandler)
      );
  }

  /// <summary>
  /// PUT: End a booking
  /// /TherapistDashboard/endbooking/{bookingid}
  /// </summary>
  /// <returns></returns>
  public endBooking(bookingid: number): Observable<boolean> {

    const url = `${super.getBaseUrl() + '/endbooking/' + bookingid}`;

    if (!environment.production)
      console.log("Therapist Dashboard:Calling " + url);

    return this.h.put<boolean>(url, null)
      .pipe(
        map(next => {
          return next;
        }),
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, count) => {
              if (count > this.RETRY_CEILING) return throwError(error);
              return this.tokenCheckAndRefresh(error);
            }),
            delay(1000),
          ),
        ),
        catchError(this.errorHandler)
      );
  }

  /// <summary>
  /// PUT: Start a booking
  /// /TherapistDashboard/startbooking/{bookingid}
  /// </summary>
  /// <returns></returns>
  public startBooking(bookingid: number): Observable<boolean> {

    const url = `${super.getBaseUrl() + '/startbooking/' + bookingid}`;

    if (!environment.production)
      console.log("Therapist Dashboard:Calling " + url);

    return this.h.put<boolean>(url, null)
      .pipe(
        map(next => {
          return next;
        }),
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, count) => {
              if (count > this.RETRY_CEILING) return throwError(error);
              return this.tokenCheckAndRefresh(error);
            }),
            delay(1000),
          ),
        ),
        catchError(this.errorHandler)
      );
  }

  /// <summary>
  /// POST: Adds a new Booking record to the database
  /// /Booking/request
  /// </summary>
  /// <param name="request"></param>
  /// <returns></returns>
  public bookingRequest(request: BookingRequest): Observable<BookingResponse> {

    let url = `${super.getBaseUrl() + '/addtherapistbooking/'}`;

    if (!environment.production)
      console.log("Booking Request:Calling " + url, request);

    return this.h.post<BookingResponse>(url, request)
      .pipe(
        map(next => {
          let o: BookingResponse = Object.assign(new BookingResponse(), next);
          return o;
        }),
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, count) => {
              if (count > this.RETRY_CEILING) return throwError(error);
              return this.tokenCheckAndRefresh(error);
            }),
            delay(1000),
          ),
        ),
        catchError(this.errorHandler)
      );
  }

  /// <summary>
  /// PUT: Force start a booking
  /// /TherapistDashboard/forcestartbooking/{bookingid}
  /// </summary>
  /// <returns></returns>
  public forceStartBooking(bookingid: number): Observable<boolean> {

    const url = `${super.getBaseUrl() + '/forcestartbooking/' + bookingid}`;

    if (!environment.production)
      console.log("Therapist Dashboard:Calling " + url);

    return this.h.put<boolean>(url, null)
      .pipe(
        map(next => {
          return next;
        }),
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, count) => {
              if (count > this.RETRY_CEILING) return throwError(error);
              return this.tokenCheckAndRefresh(error);
            }),
            delay(1000),
          ),
        ),
        catchError(this.errorHandler)
      );
  }

  /// <summary>
  /// PUT: Booking Did Not Happen
  /// /TherapistDashboard/bookingdidnothappen/{bookingid}
  /// </summary>
  /// <returns></returns>
  public bookingDidNotHappen(bookingid: number): Observable<boolean> {

    const url = `${super.getBaseUrl() + '/bookingdidnothappen/' + bookingid}`;

    if (!environment.production)
      console.log("Therapist Dashboard:Calling " + url);

    return this.h.put<boolean>(url, null)
      .pipe(
        map(next => {
          return next;
        }),
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, count) => {
              if (count > this.RETRY_CEILING) return throwError(error);
              return this.tokenCheckAndRefresh(error);
            }),
            delay(1000),
          ),
        ),
        catchError(this.errorHandler)
      );
  }

  /// <summary>
  /// PUT: Set AcceptBooking flag
  /// /TherapistDashboard/acceptbookings/{accept}
  /// </summary>
  /// <returns></returns>
  public setAcceptBookings(accept: boolean): Observable<boolean> {

    const url = `${super.getBaseUrl() + '/acceptbookings/' + accept}`;

    if (!environment.production)
      console.log("Therapist Dashboard:Calling " + url);

    return this.h.put<boolean>(url, null)
      .pipe(
        map(next => {
          return next;
        }),
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, count) => {
              if (count > this.RETRY_CEILING) return throwError(error);
              return this.tokenCheckAndRefresh(error);
            }),
            delay(1000),
          ),
        ),
        catchError(this.errorHandler)
      );
  }
}
